import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Typography, Stack } from '@mui/material';
import EditSquareIcon from '@mui/icons-material/EditSquare';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ListFiltersWidget from '../../components/widgets/list-filters.widget';
import { deleteItemApi } from '../../apis/admin';
import {
  getApiErrorMessage,
  getFormattedDate,
  prepareAndGetMediaURL,
} from '../../utilities/helper';
import { showSnackbar } from '../../store/slices/common.slice';
import {
  APIResponse,
  FreeKeyObject,
  Item,
  ListingResponse,
  Pagination,
} from '../../utilities/types';
import AdminGridWidget from '../../components/widgets/admin-grid.widget';
import FormModal from '../../components/modals/form.modal';
import CustomSwal from '../../components/elements/custom-swal.element';
import { getCatalogItems } from '../../apis';
import ItemForm from '../../components/forms/item.form';

const AdminItemsPage: React.FC = () => {
  const dispatch = useDispatch();
  const [records, setRecords] = useState<any[]>([]);
  const [page, setPage] = useState<number>(1);
  const [filterValues, setFilterValues] = useState<{ [key: string]: any }>({});
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<Item | null>(null);

  const fetch = useCallback(async () => {
    try {
      const payload = {
        filters: { ...filterValues },
        page,
      };
      setLoadingData(true);
      const response = (await getCatalogItems(payload)) as APIResponse<ListingResponse>;
      setRecords(response.data.items);
      setPagination(response.data.pagination);
    } catch (err: any) {
      dispatch(showSnackbar({ message: getApiErrorMessage(err), severity: 'error' }));
    }
    setLoadingData(false);
  }, [filterValues, dispatch, page]);

  useEffect(() => {
    fetch();
  }, [fetch, page]);

  const deleteRecord = useCallback(
    async (id: number) => {
      try {
        const response = (await deleteItemApi(id)) as APIResponse<FreeKeyObject>;
        if (response.status) {
          if (page > 1) {
            setPage(1);
          } else {
            fetch();
          }
          CustomSwal.fire({
            icon: 'success',
            title: 'Item deleted successfully',
          });
        } else {
          throw new Error(response.message || 'Error in deleting item');
        }
      } catch (err) {
        dispatch(showSnackbar({ message: getApiErrorMessage(err), severity: 'error' }));
      }
    },
    [dispatch, fetch, page]
  );

  const askDeleteConfirmation = useCallback(
    (id: number) => {
      CustomSwal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
      }).then((result) => {
        if (result.isConfirmed) {
          deleteRecord(id);
        }
      });
    },
    [deleteRecord]
  );

  const getRecordCTA = (record: Item) => {
    return (
      <Stack flexDirection="row" gap={1}>
        <Button
          variant="contained"
          size="small"
          color="info"
          title="Click to edit"
          onClick={() => {
            setShowForm(true);
            setSelectedRecord(record);
          }}
          sx={{ minWidth: 'auto' }}
        >
          <EditSquareIcon />
        </Button>
        <Button
          variant="contained"
          size="small"
          color="error"
          title="Click to delete"
          onClick={() => {
            askDeleteConfirmation(record.id);
          }}
          sx={{ minWidth: 'auto' }}
        >
          <DeleteIcon />
        </Button>
      </Stack>
    );
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 100,
    },
    {
      field: 'image',
      headerName: 'Image',
      width: 150,
      renderCell: (params: any) => (
        <img
          alt="Item"
          src={prepareAndGetMediaURL(params.value)}
          style={{ maxWidth: 60, height: 'auto', maxHeight: 60, objectFit: 'cover' }}
        />
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
    },
    {
      field: 'is_active',
      headerName: 'Active',
      flex: 1,
      renderCell: (params: any) => {
        return params.value ? 'Yes' : 'No';
      },
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      flex: 1,
      renderCell: (params: any) => {
        return getFormattedDate(params.value);
        // return dayjs.utc(utcIso).local().format('YYYY-MM-DD HH:mm')
        // return params.value ? 'Yes' : 'No';
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: any) => getRecordCTA(params.row),
    },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Items</Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          variant="contained"
          color="info"
          onClick={() => {
            setShowForm(true);
          }}
        >
          Add New Item
        </Button>
      </Box>
      <Box sx={{ mb: 2 }}>
        <ListFiltersWidget
          filters={[
            {
              name: 'name',
              label: 'Name',
              type: 'text',
            }
          ]}
          onSearch={setFilterValues}
        />
      </Box>
      <AdminGridWidget
        columns={columns}
        rows={records}
        page={page}
        onPageChange={setPage}
        loading={loadingData}
        totalRows={pagination ? pagination.total : 0}
      />
      {showForm && (
        <FormModal
          open={showForm}
          onClose={() => {
            setShowForm(false);
            setSelectedRecord(null);
          }}
          size="sm"
          title={selectedRecord?.id ? 'Edit Item' : 'Add New Item'}
        >
          <ItemForm
            item={selectedRecord}
            onSuccess={() => {
              setShowForm(false);
              setSelectedRecord(null);
              if (page > 1) {
                setPage(1);
              } else {
                fetch();
              }
              CustomSwal.fire({
                icon: 'success',
                title: 'Item saved successfully',
              });
            }}
          />
        </FormModal>
      )}
    </>
  );
};

export default AdminItemsPage;
